/**
 * FIN-11304 / FIN-12857 — adapts the BE's scenario listing to what ScenarioBar
 * renders.
 *
 * BE shape (AlertScenario, as shipped on feat/scenario-listings-for-EventTimeline
 * @ c2d1b8db8a):
 *   { variantName: String, typologyId: Long, typologyName: String,
 *     variantScore: Double, eventCount: Int }
 *
 * A SCENARIO IS A VARIANT — the specific rule that fired — not a typology. That is
 * the model the BE settled on (2026-08-21), and it is what fixes the old
 * `Typology 8001` problem: the dropdown label is now a real rule name off
 * StarRocks `triggered_rule`, and the typology moves to a badge beside it.
 *
 * `variantName` is ALSO the filter key: selecting an entry refetches the timeline
 * with `?variantName=`, which the BE matches against `triggered_rule`. So the
 * value shown and the value sent are the same string — no id mapping, nothing to
 * go out of sync. This replaces the previous `?typologyId=` filter, which the BE
 * no longer reads.
 *
 * There is deliberately NO `eventIds` here, and timeline events carry no variant
 * either — so a variant's events CANNOT be derived client-side. Filtering is a
 * refetch, and the absence of `eventIds` is what tells EventTimeline not to filter
 * locally (the mock fixture DOES carry eventIds and keeps its client-side path).
 *
 * `action` is absent from the BE contract; ScenarioBar null-guards it, so live
 * entries render without that tag until it is added.
 */

// REMOVED 2026-08-21 (team lead): there is no synthetic "All events" entry. The
// dropdown lists the variants and opens on the first one.
//
// It existed so opening an alert cost ONE timeline request rather than two —
// auto-selecting a variant means the events fetch has to wait for /scenarios to
// name one. The lead's call settles that trade in favour of matching the design,
// where the pager reads "1 / 2" for a two-variant alert with no extra entry.
//
// Still exported: the mock fixture and its tests reference it, and the container
// treats a null variantName as "no filter" for the no-scenarios fallback.
import { stripTypologyPrefix } from '../../../../../../util/typology';

export const ALL_SCENARIOS_ID = 'all';

// The BE uses 0.0 and "" as its MISSING sentinels rather than null (verified in
// AlertScenariosDao: `.getOrElse(0.0)` / `.getOrElse("")`). Rendering those would
// put a literal "Score 0" and a blank typology badge on a fraud screen, which
// states something false rather than nothing. So they are normalised back to
// absent here and ScenarioBar drops the tag.
//
// REMOVE THIS once the BE sends null/omits the field — a genuine zero-contribution
// variant is conceivable, and this cannot tell one from a failed lookup. Raised
// with the BE 2026-08-21.
// Not rounded: ScenarioBar shows it to 2dp, as Classic's Explanation does
// (FIN-13725). This is the path live scores take; the meta mappers only fill gaps.
const scoreOrUndefined = (value) =>
  typeof value === 'number' && value !== 0 ? value : undefined;

const textOrUndefined = (value) =>
  typeof value === 'string' && value.trim() ? value : undefined;

export const toScenarioList = (payload) => {
  const raw = payload?.scenarios;
  if (!Array.isArray(raw) || !raw.length) return [];

  const scenarios = raw
    // No variantName means nothing to label the row with AND nothing to send
    // back as a filter — the entry would be a dropdown row that does nothing.
    .filter((s) => s && textOrUndefined(s.variantName))
    .map((s) => ({
      // The variant name IS the identity: it is the dropdown label, the filter
      // value, and what SCORE_CONTRIBUTIONS keys on. Unique per alert in
      // practice (one row per fired rule).
      id: s.variantName,
      // Sent back verbatim as ?variantName= on selection.
      variantName: s.variantName,
      name: s.variantName,
      // Badged beside the dropdown. Undefined when the BE could not resolve it.
      //
      // FIN-13565 — STRIPPED, like every other place a typology name is shown.
      // AFC-uploaded names carry a literal "Typology" prefix baked into the
      // value itself (FIN-11922 / FIN-12130), so the card read
      // "TypologyUnusual Transaction Amount vs …" as one run-on string. This was
      // the ONLY display boundary in the shell passing the name through raw —
      // toScenarioMeta:104/192, toVariantScenarios:94 and ExplanationPanel:58
      // all normalise it, which is why the Explanation table on the SAME page
      // rendered the same typology cleanly.
      //
      // The card prepends no label of its own; there is no "Typology" string in
      // ScenarioBar. The prefix is in the data, and this repo's settled approach
      // is to normalise at display and leave the stored value alone.
      typology: textOrUndefined(
        stripTypologyPrefix(s.typologyName || '').trim()
      ),
      typologyId:
        typeof s.typologyId === 'number' && s.typologyId !== 0
          ? s.typologyId
          : undefined,
      // Already 0-100 on this contract (reads 45 / 38 / 30), NOT the 0-1
      // riskScore the timeline's toScore scales — running it through that would
      // render 4500.
      score: scoreOrUndefined(s.variantScore),
      eventCount: typeof s.eventCount === 'number' ? s.eventCount : undefined
    }));

  // BE order is preserved — it sorts by variantScore DESC, so "1." is the variant
  // that contributed most. PRD §3 asks for timestamp order instead, which needs a
  // field AlertScenario does not carry yet.
  return scenarios;
};

export default toScenarioList;
